import React, { useMemo, useState } from "react";
import Table from "../../../../components/Table";
import TitleButtons from "../../../../components/TitleButtons";
import { useSaidasSuprimentos, SaidaRowMeta } from "./Saidas-servicoData";

type DiretoriaRowUI = [
  string, // OS
  string, // Código
  string, // Nome do Produto
  string, // Quantidade
  string, // Custo Unitário
  string, // Técnico
  string, // Data
  string // Hora
];

type ItemSolicitadoModal = {
  originalIndex: number;
  os: string;
  codigo: string;
  produto: string;
  quantidadeBaixada: number;
  quantidadeSolicitada: number | null;
  custoUnitario: string;
  data: string;
  hora: string;
  meta: SaidaRowMeta;
};

type ModalState = {
  open: boolean;
  os: string;
  itens: ItemSolicitadoModal[];
};

const API_APROVAR =
  "https://api.eva-googlemarine.com/departamentos/suprimentos/retornos/aprovar";

const API_NEGAR =
  "https://api.eva-googlemarine.com/departamentos/suprimentos/retornos/negar";

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
  padding: 16,
};

const modalStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 1100,
  background: "#ffffff",
  border: "1px solid #dfe3ea",
  boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
  overflow: "hidden",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const modalHeaderStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#f8fafc",
};

const modalTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: "#14498c",
};

const modalBodyStyle: React.CSSProperties = {
  padding: 20,
  overflowY: "auto",
};

const modalFooterStyle: React.CSSProperties = {
  padding: "16px 20px",
  borderTop: "1px solid #e5e7eb",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  background: "#f8fafc",
};

const fieldBoxStyle: React.CSSProperties = {
  border: "1px solid #14498c",
  padding: "10px 12px",
  background: "#ffffff",
};

const fieldLabelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "#14498c",
  marginBottom: 4,
};

const fieldValueStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#111827",
  fontWeight: 500,
  wordBreak: "break-word",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: "0 0 12px 0",
  fontSize: 14,
  fontWeight: 600,
  color: "#14498c",
};

const textareaStyle: React.CSSProperties = {
  width: "100%",
  border: "1px solid #14498c",
  padding: "10px 12px",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
  minHeight: 100,
  resize: "vertical",
  fontFamily: "inherit",
};

const buttonPrimaryStyle: React.CSSProperties = {
  border: "1px solid #000000",
  background: "#5F8830",
  color: "#FFFFFF",
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

const buttonDangerStyle: React.CSSProperties = {
  border: "1px solid #000000",
  background: "#7D1A1A",
  color: "#ffffff",
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

const buttonSecondaryStyle: React.CSSProperties = {
  border: "1px solid #000000",
  background: "#ffffff",
  color: "#111827",
  padding: "10px 14px",
  fontSize: 14,
  cursor: "pointer",
};

function parseQuantidade(value: string | number | null | undefined): number {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value !== "string") return 0;

  const cleaned = value.replace(/\./g, "").replace(",", ".").trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function formatIsoToBR(iso: string | null | undefined): string {
  if (!iso) return "-";

  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  return d.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
  });
}

const DiretoriaServicosSaidas: React.FC = () => {
  const headers = [
    "OS",
    "Código",
    "Nome do Produto",
    "Quantidade",
    "Custo Unitário",
    "Técnico",
    "Data",
    "Hora",
  ];

  const columnWidths = [
    "110px",
    "140px",
    "360px",
    "110px",
    "140px",
    "180px",
    "110px",
    "100px",
  ];

  const { rows, rowMeta, loading, refresh } = useSaidasSuprimentos();

  const rowsFormatted = useMemo<DiretoriaRowUI[]>(() => {
    return rows.map((r) => [
      r[0] ?? "", // OS
      r[2] ?? "", // Código
      r[3] ?? "", // Nome do Produto
      r[4] ?? "", // Quantidade
      r[5] ?? "", // Custo Unitário
      r[6] ?? "", // Técnico
      r[7] ?? "", // Data
      r[8] ?? "", // Hora
    ]);
  }, [rows]);

  const [modal, setModal] = useState<ModalState>({
    open: false,
    os: "",
    itens: [],
  });

  const [motivoNegativa, setMotivoNegativa] = useState("");
  const [submittingApprove, setSubmittingApprove] = useState(false);
  const [submittingReject, setSubmittingReject] = useState(false);

  const solicitacoesIdsUnicos = useMemo(() => {
    return Array.from(
      new Set(
        modal.itens
          .map((item) => item.meta?.retornoSolicitacaoId)
          .filter((id): id is number => typeof id === "number" && id > 0)
      )
    );
  }, [modal.itens]);

  const openModalPorOS = (os: string) => {
    const itensSolicitadosDaOS: ItemSolicitadoModal[] = rows
      .map((rowOriginal, index) => {
        const meta = rowMeta[index];
        if (!meta) return null;
        if ((rowOriginal[0] ?? "") !== os) return null;
        if (meta.retornoStatus !== "solicitado") return null;

        return {
          originalIndex: index,
          os: rowOriginal[0] ?? "",
          codigo: rowOriginal[2] ?? "",
          produto: rowOriginal[3] ?? "",
          quantidadeBaixada: parseQuantidade(rowOriginal[4]),
          quantidadeSolicitada: meta.retornoQuantidade,
          custoUnitario: rowOriginal[5] ?? "",
          data: rowOriginal[7] ?? "",
          hora: rowOriginal[8] ?? "",
          meta,
        };
      })
      .filter(Boolean) as ItemSolicitadoModal[];

    setModal({
      open: true,
      os,
      itens: itensSolicitadosDaOS,
    });

    setMotivoNegativa("");
  };

  const closeModal = () => {
    if (submittingApprove || submittingReject) return;

    setModal({
      open: false,
      os: "",
      itens: [],
    });
    setMotivoNegativa("");
  };

  async function aprovarSolicitacoes() {
    if (!solicitacoesIdsUnicos.length) {
      alert("Não há solicitações pendentes para aprovar.");
      return;
    }

    setSubmittingApprove(true);

    try {
      for (const solicitacaoId of solicitacoesIdsUnicos) {
        const res = await fetch(API_APROVAR, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            solicitacao_id: solicitacaoId,
            analisado_por_id: 999,
            analisado_por_nome: "Diretor Logado",
          }),
        });

        const json = await res.json().catch(() => null);

        if (!res.ok || !json?.ok) {
          throw new Error(
            json?.erro || `Falha ao aprovar solicitação ${solicitacaoId}`
          );
        }
      }

      await refresh();
      closeModal();
      alert("Solicitação(ões) aprovada(s) com sucesso.");
    } catch (error: any) {
      alert(error?.message || "Falha ao aprovar.");
    } finally {
      setSubmittingApprove(false);
    }
  }

  async function negarSolicitacoes() {
    if (!solicitacoesIdsUnicos.length) {
      alert("Não há solicitações pendentes para recusar.");
      return;
    }

    setSubmittingReject(true);

    try {
      for (const solicitacaoId of solicitacoesIdsUnicos) {
        const res = await fetch(API_NEGAR, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            solicitacao_id: solicitacaoId,
            analisado_por_id: 999,
            analisado_por_nome: "Diretor Logado",
            motivo_negativa: motivoNegativa.trim() || null,
          }),
        });

        const json = await res.json().catch(() => null);

        if (!res.ok || !json?.ok) {
          throw new Error(
            json?.erro || `Falha ao recusar solicitação ${solicitacaoId}`
          );
        }
      }

      await refresh();
      closeModal();
      alert("Solicitação(ões) recusada(s) com sucesso.");
    } catch (error: any) {
      alert(error?.message || "Falha ao recusar.");
    } finally {
      setSubmittingReject(false);
    }
  }

  return (
    <div>
      <TitleButtons title="Diretoria - Saídas serviços" buttons={[]} />

      {loading ? (
        <div style={{ padding: 16 }}>Carregando...</div>
      ) : (
        <Table
          headers={headers}
          rows={rowsFormatted}
          columnWidths={columnWidths}
          isTableStandby={true}
          osColumnIndex={0}
          onDoubleClick={(_viewIndex, rowData) => {
            const os = String(rowData[0] ?? "");
            if (!os) return;
            openModalPorOS(os);
          }}
          getRowStyle={(_rowData, _viewIndex, originalIndex) => {
            const status = rowMeta[originalIndex]?.retornoStatus;

            if (status === "solicitado") return { background: "#FFD5CD" };
            if (status === "negado") return { background: "#f8d7da" };
            if (status === "aprovado") return { background: "#dcfce7" };

            return undefined;
          }}
        />
      )}

      {modal.open && (
        <div style={overlayStyle} onClick={closeModal}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <div style={modalHeaderStyle}>
              <div>
                <h3 style={modalTitleStyle}>
                  Análise de solicitação de retorno
                </h3>
                <div style={{ marginTop: 6, color: "#374151", fontSize: 14 }}>
                  O.S. <strong>{modal.os}</strong>
                </div>
              </div>

              <button
                type="button"
                onClick={closeModal}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 22,
                  lineHeight: 1,
                  cursor: "pointer",
                  color: "#6b7280",
                }}
              >
                ×
              </button>
            </div>

            <div style={modalBodyStyle}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 12,
                  marginBottom: 20,
                }}
              >
                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>O.S.</span>
                  <div style={fieldValueStyle}>{modal.os}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Itens solicitados</span>
                  <div style={fieldValueStyle}>{modal.itens.length}</div>
                </div>

                <div style={fieldBoxStyle}>
                  <span style={fieldLabelStyle}>Solicitações pendentes</span>
                  <div style={fieldValueStyle}>
                    {solicitacoesIdsUnicos.length}
                  </div>
                </div>
              </div>

              <h4 style={sectionTitleStyle}>Itens solicitados para retorno</h4>

              {modal.itens.length === 0 ? (
                <div
                  style={{
                    border: "1px solid #d1d5db",
                    background: "#f9fafb",
                    color: "#374151",
                    padding: 14,
                  }}
                >
                  Não existem itens com solicitação pendente para esta O.S.
                </div>
              ) : (
                <div
                  style={{
                    border: "1px solid #d1d5db",
                    overflow: "hidden",
                    marginBottom: 20,
                  }}
                >
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: 14,
                    }}
                  >
                    <thead>
                      <tr style={{ background: "#14498c", color: "#ffffff" }}>
                        <th
                          style={{
                            padding: 10,
                            border: "1px solid #d1d5db",
                            textAlign: "left",
                          }}
                        >
                          Código
                        </th>
                        <th
                          style={{
                            padding: 10,
                            border: "1px solid #d1d5db",
                            textAlign: "left",
                          }}
                        >
                          Produto
                        </th>
                        <th
                          style={{
                            padding: 10,
                            border: "1px solid #d1d5db",
                            textAlign: "center",
                            width: 120,
                          }}
                        >
                          Qtd. baixada
                        </th>
                        <th
                          style={{
                            padding: 10,
                            border: "1px solid #d1d5db",
                            textAlign: "center",
                            width: 140,
                          }}
                        >
                          Qtd. solicitada
                        </th>
                        <th
                          style={{
                            padding: 10,
                            border: "1px solid #d1d5db",
                            textAlign: "center",
                            width: 140,
                          }}
                        >
                          Custo Unitário
                        </th>
                        <th
                          style={{
                            padding: 10,
                            border: "1px solid #d1d5db",
                            textAlign: "center",
                            width: 180,
                          }}
                        >
                          Data da solicitação
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {modal.itens.map((item, idx) => (
                        <tr
                          key={`${item.meta.retornoSolicitacaoId ?? "sem"}-${
                            item.originalIndex
                          }`}
                          style={{
                            background: idx % 2 === 0 ? "#fbfcfe" : "#f6f8fb",
                          }}
                        >
                          <td
                            style={{ padding: 10, border: "1px solid #d1d5db" }}
                          >
                            {item.codigo}
                          </td>

                          <td
                            style={{ padding: 10, border: "1px solid #d1d5db" }}
                          >
                            <div>{item.produto}</div>

                            {item.meta.retornoMotivo && (
                              <div
                                style={{
                                  marginTop: 6,
                                  fontSize: 12,
                                  color: "#6b7280",
                                }}
                              >
                                <strong>Motivo:</strong>{" "}
                                {item.meta.retornoMotivo}
                              </div>
                            )}

                            {item.meta.retornoObservacao && (
                              <div
                                style={{
                                  marginTop: 4,
                                  fontSize: 12,
                                  color: "#6b7280",
                                }}
                              >
                                <strong>Obs.:</strong>{" "}
                                {item.meta.retornoObservacao}
                              </div>
                            )}
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                            }}
                          >
                            {item.quantidadeBaixada}
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                              fontWeight: 600,
                            }}
                          >
                            {item.quantidadeSolicitada ?? "-"}
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                            }}
                          >
                            {item.custoUnitario}
                          </td>

                          <td
                            style={{
                              padding: 10,
                              border: "1px solid #d1d5db",
                              textAlign: "center",
                              fontSize: 12,
                            }}
                          >
                            {formatIsoToBR(item.meta.retornoCriadoEm)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {modal.itens.length > 0 && (
                <>
                  <h4 style={sectionTitleStyle}>Motivo da recusa</h4>
                  <textarea
                    value={motivoNegativa}
                    onChange={(e) => setMotivoNegativa(e.target.value)}
                    style={textareaStyle}
                    placeholder="Preencha somente se desejar recusar a solicitação"
                  />
                </>
              )}
            </div>

            <div style={modalFooterStyle}>
              <div style={{ fontSize: 13, color: "#374151" }}>
                {modal.itens.length > 0
                  ? `${modal.itens.length} item(ns) solicitado(s)`
                  : "Nenhuma solicitação pendente"}
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  type="button"
                  onClick={closeModal}
                  style={buttonSecondaryStyle}
                  disabled={submittingApprove || submittingReject}
                >
                  Fechar
                </button>

                {modal.itens.length > 0 && (
                  <>
                    <button
                      type="button"
                      onClick={negarSolicitacoes}
                      style={buttonDangerStyle}
                      disabled={submittingApprove || submittingReject}
                    >
                      {submittingReject ? "Recusando..." : "Recusar"}
                    </button>

                    <button
                      type="button"
                      onClick={aprovarSolicitacoes}
                      style={buttonPrimaryStyle}
                      disabled={submittingApprove || submittingReject}
                    >
                      {submittingApprove ? "Aprovando..." : "Aprovar"}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiretoriaServicosSaidas;
